import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputBase,
  Box,
  Typography,
  CircularProgress,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  label?: string;
  placeholder?: string;
  onChange: (selected: string) => void;
  className?: string;
  onBlurred?: () => void;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  maxHeight?: number;
  searchPlaceholder?: string;
}

// Define the type of the methods that will be available on the ref
export interface SearchableSelectRef {
  reset: () => void;
  setSelectedOption: (value: string) => void;
}

// Styled components
const StyledSelect = styled(Select)(({ theme }) => ({
  width: "100% !important",
  "& .MuiSelect-select": {
    padding: "14px 16px",
    fontSize: "15px",
    fontWeight: 500,
    color: "#59585B",
    backgroundColor: "#fff",
    borderRadius: "3px",
    transition: "all 0.2s ease",
    width: "100%",
    "&.Mui-disabled": {
      color: "#838285",
    },
    "&.MuiSelect-displayEmpty": {
      color: "#cbd2d9",
    },
  },
  "&.Mui-disabled .MuiSelect-select": {
    backgroundColor: "#F5F7F7",
    color: "#838285",
  },
  "& .MuiSelect-icon": {
    color: "#59585B",
    right: "12px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  padding: "12px 18px",
  fontSize: "15px",
  fontWeight: 500,
  color: "#59585B",
  minHeight: "48px",
  borderBottom: "1px solid #F3F4F6",
  "&:last-child": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: "#F3F4F6",
  },
  "&.Mui-selected": {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    "&:hover": {
      backgroundColor: "#E3F2FD",
    },
  },
}));

const SearchInput = styled(InputBase)(() => ({
  width: "100%",
  fontSize: "15px",
  fontWeight: 500,
  color: "#59585B",
  background: "#F5F7F7",
  border: "1px solid #CBD2D9",
  borderRadius: 3,
  padding: "10px 12px 10px 44px",
  transition: "all 0.2s ease",
  "&:focus-within": {
    borderColor: "#19B5FE",
    borderWidth: "1.5px",
    background: "#fff",
    boxShadow: "0 0 0 2px rgba(25, 181, 254, 0.08)",
  },
  "& input": {
    padding: 0,
    background: "transparent",
    width: "100%",
    "&::placeholder": {
      color: "#9CA3AF",
      opacity: 1,
    },
  },
}));

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
}

const SearchableSelect = forwardRef<SearchableSelectRef, SearchableSelectProps>(
  (
    {
      options,
      placeholder = "Select an option",
      onChange,
      className,
      onBlurred,
      disabled = false,
      loading = false,
      error = false,
      helperText,
      maxHeight = 320,
      searchPlaceholder = "Search...",
    }: SearchableSelectProps,
    ref
  ) => {
    const [selectedValue, setSelectedValue] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const selectRef = useRef<HTMLElement>(null);

    // Debounced search
    const debouncedSearch = useDebouncedValue(searchQuery, 80);

    // Filtered options
    const filteredOptions = useMemo(() => {
      if (!debouncedSearch.trim()) {
        return options.slice(0, 100); // Limit initial options
      }

      const query = debouncedSearch.toLowerCase();
      const results: Option[] = [];

      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        if (
          option.label.toLowerCase().includes(query) ||
          option.value.toLowerCase().includes(query)
        ) {
          results.push(option);
          if (results.length >= 50) break;
        }
      }

      return results;
    }, [options, debouncedSearch]);

    // Handle select change
    const handleChange = useCallback(
      (event: any) => {
        const value = event.target.value;
        console.log("SearchableSelect onChange:", { value });
        setSelectedValue(value);
        onChange(value);
      },
      [onChange]
    );

    // Handle dropdown open
    const handleOpen = useCallback(() => {
      console.log("SearchableSelect handleOpen");
      if (!disabled) {
        setIsOpen(true);
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 50);
      }
    }, [disabled]);

    // Handle dropdown close
    const handleClose = useCallback(() => {
      console.log("SearchableSelect handleClose");
      setIsOpen(false);
      setSearchQuery("");
      if (onBlurred) onBlurred();
    }, [onBlurred]);

    // Handle search input change
    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      []
    );

    // Handle search input click
    const handleSearchClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    // Reset function
    const reset = useCallback(() => {
      setSelectedValue("");
      setSearchQuery("");
    }, []);

    // Set selected option function
    const setSelectedOption = useCallback((value: string) => {
      setSelectedValue(value || "");
    }, []);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      reset,
      setSelectedOption,
    }));

    // Render value function
    const renderValue = useCallback(
      (selected: any) => {
        if (!selected || selected === "") {
          return (
            <span style={{ color: "#cbd2d9", fontWeight: 500 }}>
              {placeholder}
            </span>
          );
        }
        const selectedOption = options.find(
          (option) => option.value === selected
        );
        return selectedOption ? selectedOption.label : placeholder;
      },
      [options, placeholder]
    );

    return (
      <Box className={className} sx={{ width: "100%" }}>
        <FormControl
          fullWidth
          error={error}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              background: "#fff",
              fontSize: "16px",
              fontWeight: 500,
              borderRadius: "3px",
              width: "100%",
              border: "1px solid #CBD2D9",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#19B5FE",
                borderWidth: "1.5px",
              },
              "&.Mui-focused": {
                borderColor: "#19B5FE",
                borderWidth: "1.5px",
                boxShadow: "0 0 0 2px rgba(25, 181, 254, 0.12)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#F5F7F7",
                borderColor: "#E5E7EB",
              },
              "& fieldset": {
                border: "none",
              },
            },
          }}
        >
          <StyledSelect
            ref={selectRef}
            disabled={disabled}
            onBlur={onBlurred}
            value={selectedValue}
            onChange={handleChange}
            className={className}
            displayEmpty
            variant="outlined"
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            renderValue={renderValue}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: maxHeight,
                  borderRadius: "8px",
                  border: "1px solid #CBD2D9",
                  boxShadow: "0 8px 32px 0 rgba(25, 181, 254, 0.10)",
                  marginTop: "4px",
                },
              },
              anchorOrigin: {
                vertical: "bottom" as const,
                horizontal: "left" as const,
              },
              transformOrigin: {
                vertical: "top" as const,
                horizontal: "left" as const,
              },
            }}
          >
            {/* Search Input */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 2,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <Box sx={{ position: "relative", padding: "12px" }}>
                <SearchIcon
                  style={{
                    position: "absolute",
                    left: 20,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9CA3AF",
                    fontSize: 20,
                    zIndex: 1,
                  }}
                />
                <SearchInput
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onClick={handleSearchClick}
                  inputRef={searchInputRef}
                  sx={{ pl: 4, margin: 0 }}
                />
              </Box>
            </Box>

            {/* Loading State */}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* No Results */}
            {!loading && filteredOptions.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  {debouncedSearch
                    ? "No results found"
                    : "No options available"}
                </Typography>
              </Box>
            )}

            {/* Options */}
            {!loading &&
              filteredOptions.map((option, index) => (
                <StyledMenuItem
                  key={`${option.value}-${index}`}
                  value={option.value}
                >
                  <ListItemText primary={option.label} />
                </StyledMenuItem>
              ))}
          </StyledSelect>
          {helperText && (
            <Typography
              variant="caption"
              color={error ? "error" : "textSecondary"}
              sx={{ mt: 0.5, ml: 1.5 }}
            >
              {helperText}
            </Typography>
          )}
        </FormControl>
      </Box>
    );
  }
);

SearchableSelect.displayName = "SearchableSelect";

export default SearchableSelect;
